import { useEffect } from 'react';
import { useRoutes } from 'react-router-dom';
import router from 'src/router';
import { SnackbarProvider } from 'notistack';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { es } from 'date-fns/locale';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import useAuth from 'src/hooks/useAuth';
import { CssBaseline } from '@mui/material';
import ThemeProvider from './theme/ThemeProvider';
import { onSnapshot } from 'firebase/firestore';
import { firestoreQP } from 'src/utils/firebaseQP';
import AppInit from './components/AppInit';
import SnackbarUtilsConfigurator from './utils/sncakbarUtilsConfigurator';
import internationalization from 'src/i18n/i18n';
import { useDispatch } from 'src/store';
// import PrivacyPolicie from './policies/privacy-policy/PrivacyPolicie';
// import TycV2 from './policies/tyc-v2';
import { setHasTicketTransfer, setHasWhatsAppEnabled } from './slices/settings/settingsSlice';
import { setAppHasPaymentRefund, setPlcHasSectors, setPlcHasSectorsLoaded } from './slices/app/appSlice';
import { setProducersSettings, setSelectedProducer } from './slices/auth/authSlice';
import { useSelector } from 'src/store';

// TODO TEMPORALMENTE SE SETTEA ESPAÑOL POR DEFECTO VALIDAR CON QA DE PRUEBAS INTEGRALES
// MULTILENGUAJE.
// FILES INVOLUCRADOS: LOGIN (descomentar "LanguageSwitcher")
// SidebarFooter/index.tsx (descomentar "LanguageSwitcher")
internationalization.changeLanguage('es');

function App() {
  const content = useRoutes(router);
  const auth = useAuth();
  const dispatch = useDispatch();

  // USE EFFECT PARA DESACTIVAR TRADUCCIÓN AUTOMÁTICA DEL NAVEGADOR
  useEffect(() => {
    // Forzar atributos translate="no" en elementos críticos
    document.documentElement.setAttribute('translate', 'no');
    document.documentElement.setAttribute('lang', 'es');
    document.body.setAttribute('translate', 'no');
    
    // Agregar clase notranslate para Google Translate
    document.body.classList.add('notranslate');
    
    // Para navegadores que soportan la API de traducción
    if (document.documentElement.hasAttribute('translate')) {
      document.documentElement.translate = false;
    }
  }, []);

  // USE EFFECT PARA SABER SI EL TENANT TIENE VARIABLE DE ENTORNO "hasPaymentRefund"
  // Y QUE SE PERMITAN REVERSIONES
  useEffect(() => {
    const settingsDocRef = firestoreQP.collection('settings').doc('config');

    const unsubscribe = onSnapshot(settingsDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        dispatch(setAppHasPaymentRefund(data.hasPaymentRefund));
        // Normaliza hasTicketTransfer si existe (string 'active' | 'inactive' o booleano)
        const raw = data.hasTicketTransfer;
        let normalized = 'inactive';
        if (typeof raw === 'string') normalized = raw;
        if (typeof raw === 'boolean') normalized = raw ? 'active' : 'inactive';
        dispatch(setHasTicketTransfer(normalized));
        dispatch(setHasWhatsAppEnabled(data?.whatsapp?.isWhatsAppEnabled || false));
        dispatch(setPlcHasSectors(data.plcHasSectors === true));
        dispatch(setPlcHasSectorsLoaded(true));
      } else {
        console.log('Config document does not exists');
        dispatch(setPlcHasSectors(false));
        dispatch(setPlcHasSectorsLoaded(true));
      }
    });

    return () => unsubscribe();
  }, []);

  // Obtener producers y selectedProducer del estado de Redux para mantenerlos sincronizados
  const { producers, selectedProducer, isMultiproducer: currentIsMultiproducer } = useSelector((state) => state.auth);

  // LISTENER PARA isMultiProducer EN settings/producers
  // Escucha cambios en tiempo real y actualiza Redux
  useEffect(() => {
    const producersDocRef = firestoreQP.collection('settings').doc('producers');

    const unsubscribe = onSnapshot(producersDocRef, (docSnap) => {
      // Si el documento NO existe, no es multiproductora
      // No hacemos nada, dejamos los valores iniciales (vacíos/nulos)
      if (!docSnap.exists()) {
        return;
      }
      
      // El documento existe - leer el valor de isMultiProducer
      const data = docSnap.data();
      const isMultiproducer = data?.isMultiProducer === true;
      
      // Actualizar Redux con el nuevo valor de isMultiproducer
      dispatch(setProducersSettings({ 
        isMultiproducer 
      }));
      
      // Si isMultiProducer es false y no hay productora seleccionada, 
      // o si cambió de true a false, seleccionar automáticamente la primera productora
      if (!isMultiproducer && Array.isArray(producers) && producers.length > 0) {
        if (!selectedProducer || currentIsMultiproducer !== isMultiproducer) {
          dispatch(setSelectedProducer(producers[0]));
        }
      }
      
      // Actualizar sessionStorage para mantener sincronía
      const savedConfig = sessionStorage.getItem('producersConfig');
      if (savedConfig) {
        try {
          const config = JSON.parse(savedConfig);
          sessionStorage.setItem('producersConfig', JSON.stringify({
            ...config,
            isMultiproducer
          }));
        } catch (e) {
          console.error('Error updating producersConfig in sessionStorage:', e);
        }
      }
    });

    return () => unsubscribe();
  }, [producers, currentIsMultiproducer, selectedProducer]);

  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
        <SnackbarProvider
          maxSnack={6}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={6000}
          preventDuplicate
        >
          <SnackbarUtilsConfigurator />
          <CssBaseline />
          {auth.isInitialized ? content : <AppInit />}
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
export default App;
